import {
  index,
  getInitialPermissions,
  get,
  getRolePermissions,
  getValidations,
  getUserRoles,
  getPermissions,
  post,
  put,
  putRolePermissions,
} from "src/api/roles-management";
import RoleForm from "src/modules/roles-management/components/RoleForm.vue";
import ActivityLogsDrawer from "src/modules/roles-management/components/ActivityLogsDrawer.vue";

const getInitialState = () => ({
  roles: [],
  initialPermissions: [],
  role: {
    _id: "",
    code: "",
    name: "",
    applicable_to: "",
    description: "",
    permissions: [],
  },
  user: {
    _id: "",
    name: "",
    role: "",
    permissions: [],
  },
});

export const useRoleManagementStore = defineStore("role-management", () => {
  const { state: formState, toggle: toggleRoleForm } = useDrawer({
    component: RoleForm,
  });
  const { toggle: toggleActivityLogsDrawer } = useDrawer({
    component: ActivityLogsDrawer,
  });

  const state = reactive(getInitialState());

  const groupPermissionsByModule = (permissions) => {
    const grouped = permissions.reduce((acc, curr) => {
      const moduleName = curr.module;

      if (!acc[moduleName]) {
        acc[moduleName] = { name: moduleName, useCases: [], visible: true };
      }

      acc[moduleName].useCases.push(curr);

      return acc;
    }, {});

    return Object.values(grouped);
  };

  const setRoles = (updatedRoles) => {
    state.roles = updatedRoles;
  };

  const setRole = (updatedRole, setInitialPermission) => {
    const { _id, code, description, name, applicable_to, rolesPermissions } =
      updatedRole;
    const permissions = groupPermissionsByModule(
      rolesPermissions[0]?.permissions || [],
    );

    if (setInitialPermission) {
      state.initialPermissions = permissions;
    }

    state.role = {
      _id,
      code,
      description,
      name,
      applicable_to: applicable_to?.[0],
      permissions,
    };
  };

  const setRolePermissions = (selectedRolePermissions) => {
    state.role = { ...state.role, permissions: selectedRolePermissions };
  };

  const setUser = (_id, user) => {
    const { name, roles, rolesPermissions } = user;
    const permissions = rolesPermissions?.length
      ? groupPermissionsByModule(rolesPermissions)
      : [];

    state.initialPermissions = permissions;

    state.user = {
      _id,
      name,
      role: roles?.[0] || "",
      permissions,
    };
  };

  const emptyUserPermissions = () => {
    state.user = { ...getInitialState().user };
  };

  const getRoles = async (options) => {
    const response = await index(options);

    setRoles(response?.data?.data);

    return response?.data?.data;
  };

  const getRolesForUser = async (options) => {
    const response = await getUserRoles(options);

    setRoles(response?.data?.data);

    return response?.data?.data;
  };

  const getRole = async (code, setInitialPermission = true) => {
    if (code) {
      const response = await get(code);

      setRole(response?.data?.response[0], setInitialPermission);
    }
  };

  const getRoleById = async (id, setInitialPermission = true) => {
    if (id) {
      const response = await getPermissions(id);

      setRole(response?.data?.data[0], setInitialPermission);
    }
  };

  const getUserRolePermissions = async (userId) => {
    if (userId) {
      const response = await getRolePermissions(userId);

      setUser(userId, response?.data?.response[0]);

      return response?.data?.response[0];
    }
  };

  const fetchInitialPermissions = async () => {
    try {
      const response = await getInitialPermissions();
      const permissions = response.data.response || [];

      state.initialPermissions = groupPermissionsByModule(permissions);
    } catch (error) {
      console.error("Error fetching permissions:", error);
    }
  };

  const getRoleValidations = async (roleId) => {
    const response = await getValidations(roleId);

    return response;
  };

  const postRole = async (role) => {
    try {
      const response = await post(role);

      if (response) {
        await getRoles();
      }
    } catch (error) {
      console.log("Error occured while creating role:", error);
    }
  };

  const putRole = async (role) => {
    try {
      const response = await put(role);

      if (response) {
        setRole(response.data.response);

        await getRoles();
      }
    } catch (error) {
      console.log("Error occurred while updating role:", error);
    }
  };

  const putUserRolePermissions = async (user) => {
    try {
      await putRolePermissions(user);

      await getRoles();
    } catch (error) {
      console.log(
        "Error occurred while updating user role and permissions:",
        error,
      );
    }
  };

  const reset = () => {
    Object.assign(state, getInitialState());
  };

  return {
    formState,
    ...toRefs(state),
    toggleRoleForm,
    toggleActivityLogsDrawer,
    setRoles,
    setUser,
    setRole,
    setRolePermissions,
    getRoles,
    getRole,
    getUserRolePermissions,
    getRoleValidations,
    getRolesForUser,
    getRoleById,
    postRole,
    putRole,
    putUserRolePermissions,
    fetchInitialPermissions,
    groupPermissionsByModule,
    emptyUserPermissions,
    reset,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useRoleManagementStore, import.meta.hot),
  );
}
